import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import { NEW_ACCOUNT } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputPassword from "@/design/forms/InputPassword";
import InputSelect from "@/design/forms/InputSelect";
import InputText from "@/design/forms/InputText";
import StringForm from "@/design/forms/StringForm";
import { showSnackbar } from "@/store/reducers/app";
import { any, number, object, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import Sesame from "@/utils/Sesame";
import { getAccessLevel, toggleLoadingState } from "@/utils/utility";
import Valkyrie, {
  viArrowLeft,
  viCircleCheck,
  viPassword,
  viPerson,
  viShieldKeyhole,
  viSpinnerThird,
  viStore,
  viXmark,
} from "@sippy-platform/valkyrie";

import {
  Box,
  Button,
  Card,
  Container,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { format, parseISO } from "date-fns";

import accountReducer from "./accountReducer";
import ChangeEmail from "./ChangeEmail";
import PbxFlyout from "./PbxFlyout";
import PbxItem from "./PbxItem";

function Account() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const auth = useSelector((state) => state.auth);
  const pbxStore = useSelector((state) => state.backstage.pbxes);
  const partnerStore = useSelector((state) => state.backstage.partners);

  const [changePassword, setChangePassword] = useState(() => id === "new");
  const [showPassword, setShowPassword] = useState(false);
  const [accountPbx, setAccountPbx] = useState("");
  const [previousId, setPreviousId] = useState(id);

  if (id !== previousId) {
    setPreviousId(id);
    setChangePassword(id === "new");
    setShowPassword(false);
  }

  // Get general data
  useEffect(() => {
    if (auth.accessLevel === 0) {
      Promise.all([Api.getPbxes(), Api.getPartners()]);
    } else {
      Promise.resolve(Api.getPbxes());
    }
  }, [auth.accessLevel]);

  const {
    value: account,
    dispatch: accountDispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_ACCOUNT,
    reducer: accountReducer,
    validator: () => ({
      firstname: string().required({ message: "First name is required." }).length([1, 40], {
        message: "First name must be at most 40 characters.",
      }),
      lastName: string().length([1, 40], {
        message: "Last name must be at most 40 characters.",
      }),
      email: string()
        .required({ message: "Email address is required." })
        .email({ message: "Email address isn't a valid format." }),
      password: string()
        .required({
          message: "Password is required.",
          condition: changePassword,
        })
        .length([8], {
          message: "Password must be at least 8 characters.",
          condition: changePassword,
        })
        .pattern("^(?=.*?[a-zA-Z])(?=.*?[0-9]).*$", {
          message: "Password should have at least one letter and one number.",
        })
        .is(account.confirmPassword, {
          message: "Passwords do not match.",
          condition: changePassword,
        }),
      accessLevel: number().required({
        message: "You must assign the user a role.",
        condition: !account.id,
      }),
      partner: object().required({
        message: "The user must be assigned to a partner.",
        condition: !account.id && account.accessLevel === 1,
      }),
      pbxes: any().truthy(account.pbxes.length >= 1, {
        message: "The user must be asigned to a PBX.",
        condition: !account.id && account.accessLevel >= 2,
      }),
    }),
    onSubmit: () => Api.storePbxAccount(account, { context: accountPbx }),
    onSuccess: (response) => {
      if (!account.id) {
        accountDispatch({ type: "id", value: response.data.account.id });
        navigate(`../${response.data.account.id}`);
      }

      if (auth.accessLevel <= 1) {
        Promise.resolve(Api.getAllAccounts());
      }

      if (account.accessLevel === 1) {
        Promise.resolve(Api.getPartner(account.partner)).then((response) => {
          accountDispatch({ type: "partner", value: response.data.partner });
        });
      }
    },
  });

  const cancelChangePassword = useCallback(() => {
    setChangePassword(false);
    accountDispatch({ type: "resetPassword", value: undefined });
    setShowPassword(false);
  }, [accountDispatch]);

  useEffect(() => {
    let ignore = false;

    if (Number(id) === account.id || !id) {
      accountDispatch({ type: "resetPassword", value: undefined });
      return;
    }

    if (id === "new") {
      Api.cancelActiveGetRequests();
      accountDispatch({
        type: "set",
        value: {
          ...NEW_ACCOUNT,
          accessLevel: auth.accessLevel === 0 ? null : 2,
        },
      });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getAccount({ id: Number(id) }, false))
      .then((response) => {
        if (!ignore) {
          setChangePassword(false);
          accountDispatch({ type: "set", value: response.data.account });
        }
      })
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [account.id, accountDispatch, auth.accessLevel, id]);

  // Handle changes
  const formHandler = (property, value) => {
    switch (property) {
      case "partner":
        accountDispatch({
          type: property,
          value: partnerStore.list.find((partner) => partner.id === value),
        });
        setSaveState(SaveState.UNSAVED);
        break;
      case "pbxes":
        accountDispatch({
          type: property,
          value: pbxStore.list.find((pbx) => pbx.context === value),
        });
        setSaveState(SaveState.UNSAVED);
        setAccountPbx(value);
        break;
      default:
        accountDispatch({ type: property, value });
        setSaveState(SaveState.UNSAVED);
    }
  };

  const accountName = useMemo(() => {
    return account.firstname || account.lastname
      ? `${account.firstname ?? ""} ${account.lastname ?? ""}`.trim()
      : "Unnamed account";
  }, [account.firstname, account.lastname]);

  // Page specific functions
  const generatePassword = () => {
    const nextPassword = new Sesame(16).generate();
    accountDispatch({ type: "resetPassword", value: nextPassword });
    setShowPassword(true);
    setSaveState(SaveState.UNSAVED);
  };

  // Partner card
  const selectPartner = (partner) => {
    navigate(`/backstage/partners/${partner.id}`);
  };

  // PBX Cards
  const accountPbxList = account.partner?.pbxes ?? account.pbxes ?? [];

  // Direct API actions
  const [pendingSendReset, setPendingSendReset] = useState(false);
  function sendResetLink() {
    setPendingSendReset(true);

    Promise.resolve(Api.postPasswordForgotten(account))
      .then(() => dispatch(showSnackbar("Password reset link send to user.")))
      .finally(() => setPendingSendReset(false));
  }

  const [pendingResendConfirmation, setPendingResendConfirmation] = useState(false);
  function resendConfirmationLink() {
    setPendingResendConfirmation(true);

    Promise.resolve(Api.postResendEmailConfirmation(account))
      .then(() => dispatch(showSnackbar("Account confirmation link send to user.")))
      .finally(() => setPendingResendConfirmation(false));
  }

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getAllAccounts()).then(() => navigate(".."));
  }

  return (
    <Container>
      <title>{`${accountName} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} />
                <Avatar text={accountName} />
              </IconButton>
            ) : (
              <Avatar text={accountName} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              {account.accessLevel > auth.accessLevel && (
                <DeleteFlow
                  key={account.id || "nk"}
                  isActionable={!!account?.id}
                  onDelete={() => Api.deleteAccount(account)}
                  name={accountName}
                  deleteInfo="Are you sure you want to delete this account? This user will no longer be able to log in on Sippy."
                  onDeleteFinish={onDeleteFinish}
                />
              )}
            </>
          }
          title={accountName}
          subheader={account.email || ""}
        />
        <FormGroup
          title="Identity"
          actions={(auth.accessLevel >= 2 || account.id === auth.id) && <ChangeEmail account={account} />}
        >
          {account.accessLevel > auth.accessLevel || account.id === auth.id || !account.id ? (
            <>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputText
                  required
                  id="firstname"
                  label="First name"
                  onChange={formHandler}
                  value={account.firstname}
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputText
                  id="lastname"
                  label="Last name"
                  onChange={formHandler}
                  value={account.lastname}
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputText
                  required
                  id="email"
                  label="Email address"
                  onChange={formHandler}
                  value={account.email}
                  error={errors}
                  disabled={auth.accessLevel >= 2 || account.id === auth.id}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <StringForm label="First name" text={account.firstname} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <StringForm label="Last name" text={account.lastname} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <StringForm label="Email address" text={account.email} />
              </Grid>
            </>
          )}
        </FormGroup>
        {!account.confirmed && (
          <FormGroup
            title="Confirm account"
            actions={
              <Stack
                direction="row"
                sx={{
                  gap: 0.5,
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => resendConfirmationLink()}
                  startIcon={
                    <Valkyrie
                      icon={pendingResendConfirmation ? viSpinnerThird : viCircleCheck}
                      spin={pendingResendConfirmation}
                    />
                  }
                  disabled={pendingResendConfirmation}
                >
                  Resend confirmation
                </Button>
              </Stack>
            }
          />
        )}
        {(account.accessLevel > auth.accessLevel || account.id === auth.id || !account.id) && (
          <FormGroup
            title="Password"
            actions={
              <Stack
                direction="row"
                sx={{
                  gap: 0.5,
                }}
              >
                {account?.id && (
                  <Button
                    onClick={sendResetLink}
                    startIcon={
                      <Valkyrie icon={pendingSendReset ? viSpinnerThird : viShieldKeyhole} spin={pendingSendReset} />
                    }
                    disabled={pendingSendReset}
                  >
                    Send password reset
                  </Button>
                )}
                {changePassword ? (
                  <Button
                    variant="contained"
                    onClick={cancelChangePassword}
                    startIcon={<Valkyrie icon={account.id ? viXmark : viPerson} />}
                  >
                    {account.id ? "Cancel" : "Let user set password"}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => setChangePassword(true)}
                    startIcon={<Valkyrie icon={viPassword} />}
                  >
                    {account.id ? "Change password" : "Set password"}
                  </Button>
                )}
              </Stack>
            }
          >
            {changePassword ? (
              <>
                <Grid size={12}>
                  <Button
                    variant="outlined"
                    onClick={() => generatePassword()}
                    startIcon={<Valkyrie icon={viShieldKeyhole} />}
                  >
                    Generate password
                  </Button>
                </Grid>
                <Grid size={12}>
                  <Typography variant="body2">
                    The new password cannot be seen after you have left this page. The user won't be notified of the
                    password. Make sure to properly and securely communicate the password with the user. A password has
                    to have at least 8 characters with a combination of letters and numbers.
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                  <InputPassword
                    required
                    showPasswordStrength
                    showPassword={showPassword}
                    id="password"
                    label="Password"
                    onChange={formHandler}
                    value={account.password}
                    error={errors}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                  <InputPassword
                    required
                    id="confirmPassword"
                    label="Confirm password"
                    onChange={formHandler}
                    value={account.confirmPassword}
                    error={errors}
                  />
                </Grid>
              </>
            ) : (
              !account?.id && (
                <Grid size={12}>
                  <Typography>The user will set their own password when validating their account.</Typography>
                </Grid>
              )
            )}
          </FormGroup>
        )}
        {auth.accessLevel <= 1 && !account.id && (
          <FormGroup title="Permissions">
            {auth.accessLevel <= 1 && (
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputSelect
                  id="accessLevel"
                  label="Role"
                  onChange={formHandler}
                  value={account.accessLevel}
                  selectValue="value"
                  selects={
                    auth.accessLevel === 0
                      ? [
                          { value: 0, title: "Admin" },
                          { value: 1, title: "Partner" },
                          { value: 2, title: "Customer" },
                          { value: 4, title: "Viewer" },
                        ]
                      : [
                          { value: 2, title: "Customer" },
                          { value: 4, title: "Viewer" },
                        ]
                  }
                  getOptionLabel={(option) => option.title}
                  helperText="The role of an account cannot be changed after saving it."
                  error={errors}
                />
              </Grid>
            )}
            {account.accessLevel === 1 && auth.accessLevel === 0 && (
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputSelect
                  id="partner"
                  label="Partner"
                  onChange={formHandler}
                  value={account.partner?.id}
                  selectValue="id"
                  selects={partnerStore.list}
                  hasEmpty={null}
                  getOptionLabel={(option) => option.name}
                  error={errors}
                />
              </Grid>
            )}
            {account.accessLevel >= 2 && auth.accessLevel <= 1 && (
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputSelect
                  id="pbxes"
                  label="PBX"
                  onChange={formHandler}
                  value={accountPbx}
                  selectValue="context"
                  selects={pbxStore.list}
                  getOptionLabel={(option) => option.name}
                  helperText="You can add more PBXes once the account is saved."
                  error={errors}
                />
              </Grid>
            )}
          </FormGroup>
        )}
        {account.id && (
          <>
            <FormGroup title="Details">
              {account.createdDate && account.createdDate !== "0000-00-00" && (
                <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                  <StringForm label="Joined" text={format(parseISO(account.createdDate), "d MMMM yyyy")} />
                </Grid>
              )}
              {account.updatedDate && account.updatedDate !== "0000-00-00" && (
                <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                  <StringForm label="Last changed" text={format(parseISO(account.updatedDate), "d MMMM yyyy")} />
                </Grid>
              )}
              {(auth.accessLevel !== 0 || account.id) && (
                <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                  <StringForm label="Role" text={getAccessLevel(account.accessLevel)} />
                </Grid>
              )}
            </FormGroup>
            {account.partner && (
              <FormGroup title="Partner">
                <Grid size={12}>
                  <Card variant="outlined">
                    <List>
                      <ListItem disablePadding>
                        <ListItemButton onClick={() => selectPartner(account.partner)} sx={{ borderRadius: 0 }}>
                          <ListItemAvatar>
                            <Avatar text={account.partner.name} icon={viStore} />
                          </ListItemAvatar>
                          <ListItemText
                            primary={account.partner.name}
                            secondary={`${accountPbxList?.length ?? 0} PBX${accountPbxList?.length === 1 ? "" : "es"}`}
                          />
                        </ListItemButton>
                      </ListItem>
                    </List>
                  </Card>
                </Grid>
              </FormGroup>
            )}
            {account.accessLevel !== 0 && (
              <FormGroup
                title="PBXes"
                actions={
                  auth.accessLevel <= 1 &&
                  account.accessLevel >= 2 &&
                  !account.partner?.id && (
                    <PbxFlyout account={account} accountDispatch={accountDispatch} accountPbxList={accountPbxList} />
                  )
                }
              >
                <Grid size={12}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(min(16rem, 100%), 1fr))",
                      gap: 1,
                    }}
                  >
                    {accountPbxList.length ? (
                      accountPbxList.map((pbx) => (
                        <PbxItem
                          key={pbx.id}
                          pbx={pbx}
                          account={account}
                          accountDispatch={accountDispatch}
                          getPbxes={accountPbxList}
                        />
                      ))
                    ) : (
                      <Typography>No PBXes linked to this account.</Typography>
                    )}
                  </Box>
                </Grid>
              </FormGroup>
            )}
          </>
        )}
      </Stack>
    </Container>
  );
}

export default Account;
